import React from 'react';
import { useWatch } from 'react-hook-form';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { FactorType } from '@/api/generated';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { OtpInput } from '@/components/ui/otp-input';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { useLanguage } from '@/contexts/language-context';
import { useOtpForm } from '@/hooks/forms/use-otp-form';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';

interface TotpFactorProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const TotpFactor: React.FC<TotpFactorProps> = ({
  submitResponse,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();

  const {
    form,
    onSubmit,
    isSubmitting,
    getErrorMessage,
    hasExceededRetries,
    handleCodeChange,
    handleCodePasted,
    handleComplete,
  } = useOtpForm({
    submitOtp: submitResponse,
    method: FactorType.TOTP,
    maxRetries: 3,
  });

  const { control, handleSubmit } = form;
  const codeValue = useWatch({ control, name: 'code' });

  const errorMessage = getErrorMessage();
  const hasError = Boolean(errorMessage);

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-1'>
            <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
              {t('mfa.verifyUsingTotp')}
            </Text>
            <Text size={TextSizes.SM} className='text-secondary'>
              Enter the 6-digit code from your authenticator app
            </Text>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='flex flex-col gap-4'>
              <OtpInput
                value={codeValue}
                onChange={handleCodeChange}
                onComplete={handleComplete}
                onCodePasted={handleCodePasted}
                hasError={hasError}
                disabled={isSubmitting || hasExceededRetries}
                autoFocus
                length={6}
              />

              {hasError && <StatusMessage>{errorMessage}</StatusMessage>}

              <LoginButton
                data-testid='submit-totp-button'
                type='submit'
                size={Sizes.LG}
                className='w-full'
                isLoading={isSubmitting}
                disabled={hasExceededRetries}
              >
                {isSubmitting ? t('auth.signingIn') : t('common.continue')}
              </LoginButton>
            </div>
          </form>
        </div>
      </LoginCard>
    </Page>
  );
};
